import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader, AdminSectionHeader } from '@/components/patterns/admin-page'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { StyledIcon, type StyledIconProps, type StyledIconTone } from '@/components/ui/styled-icon'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ActionRow } from '@/components/patterns/action-row'
import { usePathHelper, type PathLink } from '@/hooks/usePathHelper'
import { canRunStatements } from '@/lib/services/money/money.access'
import { Can, useAbility, type AppAbility } from '@/lib/casl'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import {
  ArrowRight,
  Banknote,
  BarChart3,
  BookOpenCheck,
  CalendarOff,
  FileChartColumn,
  HandCoins,
  Gauge,
  Landmark,
  MapPinned,
  Network,
  PhoneCall,
  ShieldCheck,
  UsersRound,
  Wallet,
  Send,
} from 'lucide-react'

/** Global staff reports (Closed Days, Journal entries): global access plus either report's permission. */
function canViewGlobalAdminReportsWith(ability: AppAbility): boolean {
  return (
    ability.can('access', 'global') &&
    (ability.can('view', 'closed-days-report') || ability.can('view', 'accounting-journal'))
  )
}

export function loadReportsIndex({
  context,
  params,
}: {
  context: { ability: AppAbility }
  params: { orgId: number }
}): void {
  const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
  const canViewFloristDeposits = !isGlobalOrg && context.ability.can('view', 'payouts')
  const canViewCashDrawer = !isGlobalOrg && context.ability.can('list', 'cash-drawer-reports')
  const canViewGlobalAdminReports = !isGlobalOrg && canViewGlobalAdminReportsWith(context.ability)

  if (
    !context.ability.can('list', 'transactions') &&
    !canViewFloristDeposits &&
    !canViewCashDrawer &&
    !canViewGlobalAdminReports
  ) {
    throw notFound()
  }
}

export const Route = createFileRoute('/_authenticated/$orgId/reports/')({
  component: ReportsIndexPage,
  loader: loadReportsIndex,
})

export function ReportsIndexPage() {
  const { orgId } = Route.useParams()
  const path = usePathHelper()
  const salesSummaryLink = path.salesStatistics.index().using({ orgId }).link
  const closedDaysLink = path.closedDays.index().using({ orgId }).link
  const accountingJournalLink = path.accountingJournal.index().using({ orgId }).link
  const salesTaxLink = path.salesTax.index().using({ orgId }).link
  const tipsLink = path.tips.index().using({ orgId }).link
  const phoneClicksLink = path.phoneClicks.index().using({ orgId }).link
  const externalOrdersLink = path.externalOrders.index().using({ orgId }).link
  const platformLedgerLink = path.platformLedger.index().using({ orgId }).link
  const cashDrawerLink = path.cashDrawer.index().using({ orgId }).link
  const depositsLink = path.deposits.index().using({ orgId }).link
  const floristPerformanceLink = path.floristPerformance.index().using({ orgId }).link
  const floristMapLink = path.floristMap.index().using({ orgId }).link

  return (
    <div className="@container/reports w-full max-w-6xl space-y-6 pb-6">
      <AdminPageHeader icon={FileChartColumn} title="Reports" />
      <IfGlobalOrg
        show
        fallback={
          <FloristReports
            orgId={orgId}
            salesSummaryLink={salesSummaryLink}
            closedDaysLink={closedDaysLink}
            accountingJournalLink={accountingJournalLink}
            salesTaxLink={salesTaxLink}
            tipsLink={tipsLink}
            phoneClicksLink={phoneClicksLink}
            externalOrdersLink={externalOrdersLink}
            cashDrawerLink={cashDrawerLink}
            depositsLink={depositsLink}
          />
        }
      >
        <div className="space-y-4">
          <OrganizationContextNotice>
            Global reports summarize activity across florist organizations. Florist-only reports are
            still available inside the right florist account.
          </OrganizationContextNotice>

          <Can I="access" the="global">
            <ReportCard
              link={floristPerformanceLink}
              icon={Gauge}
              iconTone="mint"
              title="Florist Performance"
              description="Compare florist order counts, payments collected, platform revenue, and recent sales trends."
            />
          </Can>

          <Can I="access" the="global">
            <ReportCard
              link={floristMapLink}
              icon={MapPinned}
              iconTone="mint"
              title="Florist Map"
              description="See where our florists are on a map, and jump to any ZIP code, city or state."
            />
          </Can>

          <Can I="list" a="transactions">
            <ReportCard
              link={salesSummaryLink}
              icon={BarChart3}
              iconTone="periwinkle"
              title="Global Payments Collected"
              description="Review money collected across florists, counted on the day payment was collected, with payouts, top florists, curated products, and transactions."
            />
          </Can>

          <Can I="list" a="transactions">
            <ReportCard
              link={platformLedgerLink}
              icon={BookOpenCheck}
              iconTone="periwinkle"
              title="Platform Ledger"
              description="Inspect our platform fee, POS fee, and processor cost ledger entries."
            />
          </Can>
        </div>
      </IfGlobalOrg>
    </div>
  )
}

type FloristReportsProps = {
  /** The `$orgId` route param, parsed to a number. */
  orgId: number
  salesSummaryLink: PathLink
  closedDaysLink: PathLink
  accountingJournalLink: PathLink
  salesTaxLink: PathLink
  tipsLink: PathLink
  phoneClicksLink: PathLink
  externalOrdersLink: PathLink
  cashDrawerLink: PathLink
  depositsLink: PathLink
}

function FloristReports({
  orgId,
  salesSummaryLink,
  closedDaysLink,
  accountingJournalLink,
  salesTaxLink,
  tipsLink,
  phoneClicksLink,
  externalOrdersLink,
  cashDrawerLink,
  depositsLink,
}: FloristReportsProps) {
  const path = usePathHelper()
  const ability = useAbility()
  return (
    <div className="space-y-8">
      <Can I="list" a="transactions">
        <ReportSection
          title="Sales"
          description="Orders placed with your shop and through other sources."
        >
          <ReportCard
            link={path.salesSummary.index().using({ orgId }).link}
            icon={BarChart3}
            iconTone="mint"
            title="Sales Summary"
            description="Daily totals for your accounting journal entries, with an order-by-order breakdown."
          />
          <ReportCard
            link={salesSummaryLink}
            icon={Wallet}
            iconTone="periwinkle"
            title="Sales Statistics"
            description="Order trends, product types, and top customers."
          />
          <ReportCard
            link={externalOrdersLink}
            icon={Network}
            iconTone="periwinkle"
            title="Incoming Wire Orders"
            description="Orders and income from Flower Shop Network and Condolences."
          />
        </ReportSection>
        <ReportSection
          title="House accounts"
          description="What customers owe and what’s past due."
          actions={
            canRunStatements(ability) ? (
              <Button asChild variant="link">
                <Link {...path.statements.index().using({ orgId }).link}>
                  <Send />
                  Create statements
                </Link>
              </Button>
            ) : null
          }
        >
          <ReportCard
            link={path.customerBalances.index().using({ orgId }).link}
            icon={UsersRound}
            iconTone="amethyst"
            title="Customer Balances"
            description="Review unpaid invoices, total balances, and how long balances are overdue."
          />
        </ReportSection>
      </Can>
      {ability.can('view', 'payouts') ? (
        <ReportSection
          title="Deposits"
          description="Credit card collections, fees, and deposits to your bank."
        >
          <Can I="view" a="payouts">
            <ReportCard
              link={depositsLink}
              icon={Landmark}
              iconTone="mint"
              title="Credit Card Deposit Report"
              description="See when money reaches your bank and which orders are included in each deposit."
            />
          </Can>
        </ReportSection>
      ) : null}
      <Can I="list" a="cash-drawer-reports">
        <ReportSection
          title="Cash management"
          description="Cash and check activity recorded through shop registers."
        >
          <ReportCard
            link={cashDrawerLink}
            icon={Banknote}
            iconTone="mint"
            title="Cash Drawer Report"
            description="Reconcile cash and checks, review cash drawer differences, and audit drawer activity."
          />
        </ReportSection>
      </Can>
      <Can I="list" a="transactions">
        <ReportSection
          title="Taxes & tips"
          description="Details for tax reporting and tracking tips."
        >
          <ReportCard
            link={salesTaxLink}
            icon={Landmark}
            iconTone="amethyst"
            title="Sales Tax Report"
            description="Review sales tax by location and tax jurisdiction for any date range."
          />
          <ReportCard
            link={tipsLink}
            icon={HandCoins}
            iconTone="mint"
            title="Tips Report"
            description="Review tips, refunded tips, and trends for any date range."
          />
        </ReportSection>
        <ReportSection title="Website" description="How visitors engage with your website.">
          <ReportCard
            link={phoneClicksLink}
            icon={PhoneCall}
            iconTone="amethyst"
            title="Mobile Call Report"
            description="See recorded phone-link clicks/taps on your website."
          />
        </ReportSection>
      </Can>

      {canViewGlobalAdminReportsWith(ability) ? (
        <ReportSection icon={ShieldCheck} title="Global Admin Reports">
          <Can I="view" a="closed-days-report">
            <ReportCard
              link={closedDaysLink}
              icon={CalendarOff}
              iconTone="periwinkle"
              title="Closed Days Report"
              description="See scheduled pickup and delivery pause coverage by location for any date range."
            />
          </Can>
          <Can I="view" a="accounting-journal">
            <ReportCard
              link={accountingJournalLink}
              icon={BookOpenCheck}
              iconTone="mint"
              title="Journal entries"
              description="Check this shop's daily double-entry journal and export it before it reaches QuickBooks."
            />
          </Can>
        </ReportSection>
      ) : null}
    </div>
  )
}

type ReportSectionProps = {
  icon?: StyledIconProps['icon']
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
}

function ReportSection({ icon, title, description, actions, children }: ReportSectionProps) {
  return (
    <section className="space-y-3" aria-label={title}>
      <AdminSectionHeader
        className="mx-5 flex-wrap items-center justify-start"
        icon={icon}
        iconTone="brand"
        title={title}
        description={description}
        actions={actions}
      />
      <div className="grid gap-4 @3xl/reports:grid-cols-2">{children}</div>
    </section>
  )
}

type ReportCardProps = {
  link: PathLink
  icon: StyledIconProps['icon']
  iconTone: StyledIconTone
  title: string
  description: string
}

function ReportCard({ link, icon, iconTone, title, description }: ReportCardProps) {
  return (
    <Card interactive size="flush" className="overflow-hidden">
      <ActionRow asChild radius="none" className="h-full gap-4 px-5 py-5">
        <Link {...link}>
          <StyledIcon icon={icon} tone={iconTone} className="shrink-0" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <CardTitle size="sm">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <ArrowRight className="size-4 shrink-0" />
        </Link>
      </ActionRow>
    </Card>
  )
}
